// features/account/documentationPrivacySlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate } from '../hooks/axiosInstance';
import axios from 'axios';

// Get documentation and privacy content
export const getDocumentationPrivacy = createAsyncThunk(
  'documentationPrivacy/getDocumentationPrivacy',
  async (_, thunkAPI) => {
    try {
      const response = await axiosPrivate.get('/documentation-privacy');
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: error.message }
      );
    }
  }
);

export const getPublicPrivacy = createAsyncThunk(
  'documentationPrivacy/getPublicPrivacy',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get('/api/privacy'); // Use regular axios, not axiosPrivate
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: error.message }
      );
    }
  }
);

// Update documentation content
export const updateDocumentation = createAsyncThunk(
  'documentationPrivacy/updateDocumentation',
  async (documentation, thunkAPI) => {
    try {
      const response = await axiosPrivate.put(
        '/documentation-privacy/documentation',
        {
          documentation,
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: error.message }
      );
    }
  }
);

// Update privacy content
export const updatePrivacy = createAsyncThunk(
  'documentationPrivacy/updatePrivacy',
  async (privacy, thunkAPI) => {
    try {
      const response = await axiosPrivate.put(
        '/documentation-privacy/privacy',
        {
          privacy,
        }
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: error.message }
      );
    }
  }
);

// Update both documentation and privacy
export const updateDocumentationPrivacy = createAsyncThunk(
  'documentationPrivacy/updateDocumentationPrivacy',
  async ({ documentation, privacy }, thunkAPI) => {
    try {
      const response = await axiosPrivate.put('/documentation-privacy', {
        documentation,
        privacy,
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: error.message }
      );
    }
  }
);

const documentationPrivacySlice = createSlice({
  name: 'documentationPrivacy',
  initialState: {
    // Content data
    documentation: '',
    privacy: '',
    lastUpdated: null,

    // Loading states
    loading: false,
    documentationLoading: false,
    privacyLoading: false,

    // UI states
    error: null,
    success: null,

    // Unsaved changes tracking
    hasUnsavedDocumentation: false,
    hasUnsavedPrivacy: false,

    // Form states
    documentationDraft: '',
    privacyDraft: '',
  },
  reducers: {
    // Clear messages
    clearMessages: (state) => {
      state.error = null;
      state.success = null;
    },

    // Update draft content (for unsaved changes tracking)
    updateDocumentationDraft: (state, action) => {
      state.documentationDraft = action.payload;
      state.hasUnsavedDocumentation = action.payload !== state.documentation;
    },

    updatePrivacyDraft: (state, action) => {
      state.privacyDraft = action.payload;
      state.hasUnsavedPrivacy = action.payload !== state.privacy;
    },

    // Reset drafts to current saved content
    resetDocumentationDraft: (state) => {
      state.documentationDraft = state.documentation;
      state.hasUnsavedDocumentation = false;
    },

    resetPrivacyDraft: (state) => {
      state.privacyDraft = state.privacy;
      state.hasUnsavedPrivacy = false;
    },

    // Reset all drafts
    resetAllDrafts: (state) => {
      state.documentationDraft = state.documentation;
      state.privacyDraft = state.privacy;
      state.hasUnsavedDocumentation = false;
      state.hasUnsavedPrivacy = false;
    },

    // Clear all unsaved changes flags
    clearUnsavedChanges: (state) => {
      state.hasUnsavedDocumentation = false;
      state.hasUnsavedPrivacy = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get documentation and privacy
      .addCase(getDocumentationPrivacy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocumentationPrivacy.fulfilled, (state, action) => {
        state.loading = false;
        state.documentation = action.payload.data.documentation;
        state.privacy = action.payload.data.privacy;
        state.lastUpdated = action.payload.data.updatedAt;

        // Initialize drafts with current content
        state.documentationDraft = action.payload.data.documentation;
        state.privacyDraft = action.payload.data.privacy;
        state.hasUnsavedDocumentation = false;
        state.hasUnsavedPrivacy = false;
      })
      .addCase(getDocumentationPrivacy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to fetch content';
      })

      // In the extraReducers section, add this case
      .addCase(getPublicPrivacy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPublicPrivacy.fulfilled, (state, action) => {
        state.loading = false;
        state.privacy = action.payload.data.privacy;
        state.lastUpdated = action.payload.data.lastUpdated;
      })
      .addCase(getPublicPrivacy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to fetch privacy policy';
      })

      // Update documentation
      .addCase(updateDocumentation.pending, (state) => {
        state.documentationLoading = true;
        state.error = null;
      })
      .addCase(updateDocumentation.fulfilled, (state, action) => {
        state.documentationLoading = false;
        state.success = action.payload.msg;
        state.documentation = action.payload.data.documentation;
        state.lastUpdated = action.payload.data.updatedAt;

        // Update draft and clear unsaved flag
        state.documentationDraft = action.payload.data.documentation;
        state.hasUnsavedDocumentation = false;
      })
      .addCase(updateDocumentation.rejected, (state, action) => {
        state.documentationLoading = false;
        state.error = action.payload?.msg || 'Failed to update documentation';
      })

      // Update privacy
      .addCase(updatePrivacy.pending, (state) => {
        state.privacyLoading = true;
        state.error = null;
      })
      .addCase(updatePrivacy.fulfilled, (state, action) => {
        state.privacyLoading = false;
        state.success = action.payload.msg;
        state.privacy = action.payload.data.privacy;
        state.lastUpdated = action.payload.data.updatedAt;

        // Update draft and clear unsaved flag
        state.privacyDraft = action.payload.data.privacy;
        state.hasUnsavedPrivacy = false;
      })
      .addCase(updatePrivacy.rejected, (state, action) => {
        state.privacyLoading = false;
        state.error = action.payload?.msg || 'Failed to update privacy policy';
      })

      // Update both documentation and privacy
      .addCase(updateDocumentationPrivacy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDocumentationPrivacy.fulfilled, (state, action) => {
        state.loading = false;
        state.success = action.payload.msg;
        state.documentation = action.payload.data.documentation;
        state.privacy = action.payload.data.privacy;
        state.lastUpdated = action.payload.data.updatedAt;

        // Update drafts and clear unsaved flags
        state.documentationDraft = action.payload.data.documentation;
        state.privacyDraft = action.payload.data.privacy;
        state.hasUnsavedDocumentation = false;
        state.hasUnsavedPrivacy = false;
      })
      .addCase(updateDocumentationPrivacy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to update content';
      });
  },
});

export const {
  clearMessages,
  updateDocumentationDraft,
  updatePrivacyDraft,
  resetDocumentationDraft,
  resetPrivacyDraft,
  resetAllDrafts,
  clearUnsavedChanges,
} = documentationPrivacySlice.actions;

export default documentationPrivacySlice.reducer;
